/*
 * Lightweight HTTP client for browser/server with base URL and JSON helpers
 */

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type QueryParamPrimitive = string | number | boolean;
export type QueryParams = Record<
  string,
  QueryParamPrimitive | QueryParamPrimitive[] | undefined | null
>;

export type NextFetchOptions = {
  revalidate?: number;
  tags?: string[];
  log?: boolean;
  logMessage?: string;
};

export interface HttpClientOptions {
  baseUrl?: string;
  defaultHeaders?: Record<string, string>;
  getAuthHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
  timeoutMs?: number;
}

export class HttpError extends Error {
  status: number;
  url: string;
  body: unknown;

  constructor(message: string, status: number, url: string, body: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

// Locale store for Accept-Language header
let currentLocale: string | null = null;

export function setHttpClientLocale(locale: string | null) {
  currentLocale = locale;
}

const isBrowser = typeof window !== 'undefined';

// App-registered auth wiring (refresh endpoint + hard-logout action).
let authConfig: { refreshUrl?: string; onLogout?: () => void } = {};
export function configureHttpAuth(cfg: { refreshUrl?: string; onLogout?: () => void }) {
  authConfig = cfg;
}

export function readXsrfCookie(): string {
  if (!isBrowser) return '';
  // Split on the first '=' only — the token value itself may contain '=' (base64 padding).
  return (
    document.cookie
      .split('; ')
      .find((c) => c.startsWith('XSRF-TOKEN='))
      ?.slice('XSRF-TOKEN='.length) ?? ''
  );
}

// Single-flight refresh shared across every client instance.
let refreshPromise: Promise<boolean> | null = null;
function tryRefresh(): Promise<boolean> {
  if (!isBrowser || !authConfig.refreshUrl) return Promise.resolve(false);
  if (!refreshPromise) {
    refreshPromise = fetch(authConfig.refreshUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-XSRF-TOKEN': readXsrfCookie() },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

const UNSAFE_METHODS = new Set<HttpMethod>(['POST', 'PUT', 'PATCH', 'DELETE']);
// Requests that must never trigger the refresh→retry→logout loop.
// Anonymous credential endpoints: a 401 here means "these credentials are
// wrong", not "the session expired" — refreshing can't help and the resulting
// onLogout() would hard-navigate away from the login page mid-flow.
const SKIP_REFRESH = [
  '/auth/refresh',
  '/auth/login',
  '/auth/register',
  '/auth/passkey/challenge',
  '/auth/passkey/verify',
];

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

// FormData goes as-is (multipart uploads); anything else is JSON. A FormData body can be re-sent,
// so uploads keep the refresh→retry on 401 like every JSON call.
function encodeBody(body: unknown): BodyInit | undefined {
  if (body === undefined) return undefined;
  if (isFormData(body)) return body;
  return JSON.stringify(body);
}

function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        search.append(key, String(item));
      }
    } else {
      search.append(key, String(value));
    }
  }
  const query = search.toString().replaceAll('+', '%20');
  return query ? `?${query}` : '';
}

export function createHttpClient(options?: HttpClientOptions) {
  const baseUrl = options?.baseUrl;
  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.defaultHeaders ?? {}),
  };

  async function request<T>(
    method: HttpMethod,
    path: string,
    init?: {
      query?: QueryParams;
      body?: unknown;
      headers?: Record<string, string>;
      signal?: AbortSignal;
      next?: NextFetchOptions;
      /**
       * When true, a failed refresh will NOT call authConfig.onLogout().
       * The 401 is still thrown so the caller's .catch() can handle it.
       * Use for boot session-probes where an anonymous 401 is expected.
       */
      suppressLogout?: boolean;
    },
    isRetry = false,
  ): Promise<T> {
    const controller = new AbortController();
    const timeout = options?.timeoutMs
      ? setTimeout(() => controller.abort(), options.timeoutMs)
      : undefined;

    try {
      const authHeaders = { ...(await options?.getAuthHeaders?.()) } as Record<string, string>;
      if (currentLocale) {
        authHeaders['Accept-Language'] = currentLocale;
      }
      // CSRF double-submit on unsafe methods (browser only).
      if (isBrowser && UNSAFE_METHODS.has(method)) {
        const xsrf = readXsrfCookie();
        if (xsrf) authHeaders['X-XSRF-TOKEN'] = xsrf;
      }
      const url = `${baseUrl}${path}${buildQuery(init?.query)}`;

      const headers: Record<string, string> = {
        ...defaultHeaders,
        ...authHeaders,
        ...(init?.headers ?? {}),
      };
      // Multipart: the browser sets Content-Type with the boundary itself.
      if (isFormData(init?.body)) delete headers['Content-Type'];

      const res = await fetch(url, {
        method,
        headers,
        body: encodeBody(init?.body),
        signal: init?.signal ?? controller.signal,
        credentials: 'include',
        next: init?.next,
      });

      const contentType = res.headers.get('content-type') ?? '';
      const isJson = contentType.includes('application/json');
      const payload = isJson ? await res.json().catch(() => undefined) : await res.text();

      if (!res.ok) {
        // Only 401 (unauthenticated / expired credentials) triggers a token refresh.
        // A 403 means the backend authenticated the request but denied access
        // (genuine authorization failure) — refreshing the token won't help and only
        // adds a wasted request, so it must NOT enter the refresh→retry→logout loop.
        const canRefresh =
          isBrowser &&
          res.status === 401 &&
          !isRetry &&
          !SKIP_REFRESH.some((p) => path.startsWith(p));
        if (canRefresh) {
          const refreshed = await tryRefresh();
          if (refreshed) {
            return request<T>(method, path, init, true);
          }
          if (!init?.suppressLogout) authConfig.onLogout?.();
        }
        throw new HttpError(`Request failed with status ${res.status}`, res.status, url, payload);
      }

      return (payload as T) ?? (undefined as unknown as T);
    } finally {
      if (timeout) clearTimeout(timeout);
    }
  }

  return {
    get: <T>(
      path: string,
      init?: {
        query?: unknown;
        headers?: Record<string, string>;
        next?: NextFetchOptions;
        suppressLogout?: boolean;
      },
    ) =>
      request<T>('GET', path, {
        ...init,
        query: (init?.query as QueryParams | undefined) ?? undefined,
      }),
    post: <T>(
      path: string,
      init?: {
        body?: unknown;
        headers?: Record<string, string>;
        query?: unknown;
        next?: NextFetchOptions;
      },
    ) =>
      request<T>('POST', path, {
        ...init,
        query: (init?.query as QueryParams | undefined) ?? undefined,
      }),
    put: <T>(
      path: string,
      init?: {
        body?: unknown;
        headers?: Record<string, string>;
        query?: unknown;
        next?: NextFetchOptions;
      },
    ) =>
      request<T>('PUT', path, {
        ...init,
        query: (init?.query as QueryParams | undefined) ?? undefined,
      }),
    patch: <T>(
      path: string,
      init?: {
        body?: unknown;
        headers?: Record<string, string>;
        query?: unknown;
        next?: NextFetchOptions;
      },
    ) =>
      request<T>('PATCH', path, {
        ...init,
        query: (init?.query as QueryParams | undefined) ?? undefined,
      }),
    delete: <T>(
      path: string,
      init?: {
        body?: unknown;
        headers?: Record<string, string>;
        query?: unknown;
        next?: NextFetchOptions;
      },
    ) =>
      request<T>('DELETE', path, {
        ...init,
        query: (init?.query as QueryParams | undefined) ?? undefined,
      }),
    baseUrl,
  };
}

export type HttpClient = ReturnType<typeof createHttpClient>;
